/**
 * SlimForm Service
 *
 * This module provides API functions for the SlimForm submission process,
 * including creating submissions, retrieving them, updating, and approving/rejecting.
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

/**
 * Bounding box coordinates for asset location in overview photo
 */
export interface BoundingBox {
	x: number;
	y: number;
	width: number;
	height: number;
}

/**
 * SHA-256 hashes per photo for OCR cache deduplication
 */
export interface PhotoHashes {
	overview: string;
	manufacturerPlate?: string;
	assetTag?: string;
}

/**
 * Field with confidence score from AI extraction
 */
export interface ConfidentField<T = string> {
	text: T;
	confidence: number;
}

/**
 * AI-extracted asset data from photos
 */
export interface ExtractedAssetData {
	assetType?: ConfidentField;
	condition?: ConfidentField;
	visibleDamage?: ConfidentField;
	equipmentArea?: ConfidentField;
	manufacturer?: ConfidentField;
	manufacturer_id?: ConfidentField<number>;
	manufacturerSource?: ConfidentField<"plate" | "overview">;
	modelNumber?: ConfidentField;
	serialNumber?: ConfidentField;
	manufacturerDate?: ConfidentField;
	refrigerantType?: ConfidentField;
	refrigerantQuantity?: ConfidentField;
	refrigerantQuantityUnit?: ConfidentField;
	powerSupplyVoltage?: ConfidentField;
	powerSupplyAmps?: ConfidentField;
	powerSupplyFrequency?: ConfidentField;
	powerSupplyPhase?: ConfidentField;
	// Performance / capacity data
	btuRating?: ConfidentField;
	tonnage?: ConfidentField;
	seerRating?: ConfidentField;
	eerRating?: ConfidentField;
	// Additional identification
	partNumber?: ConfidentField;
	rawPlateText?: ConfidentField;
	assetTag?: ConfidentField;
	// Pre-resolved IDs (set during processing to avoid redundant lookups)
	organizationAssetTypeId?: ConfidentField<number>;
	organizationEquipmentAreaId?: ConfidentField<number>;
}

/**
 * User-edited asset data (no confidence scores)
 */
export interface EditedAssetData {
	assetType?: string;
	condition?: string;
	visibleDamage?: string;
	equipmentArea?: string;
	manufacturer?: string;
	manufacturer_id?: number;
	modelNumber?: string;
	serialNumber?: string;
	manufacturerDate?: string;
	refrigerantType?: string;
	refrigerantQuantity?: string;
	refrigerantQuantityUnit?: string;
	powerSupplyVoltage?: string;
	powerSupplyAmps?: string;
	powerSupplyFrequency?: string;
	assetTag?: string;
	generalNotes?: string;
	organizationAssetTypeId?: number;
	organizationEquipmentAreaId?: number;
	// GPS coordinates for Visual Tag 2.0 integration
	gpsLatitude?: string;
	gpsLongitude?: string;
	gpsAccuracy?: string;
}

/**
 * SlimForm submission status
 */
export type SlimFormStatus =
	| "PENDING"
	| "PROCESSING"
	| "PROCESSED"
	| "APPROVED"
	| "REJECTED"
	| "FAILED";

/**
 * SlimForm submission
 */
export interface SlimFormSubmission {
	id: number;
	organizationId: number;
	locationId: number;
	userId: number;
	overviewPhotoUrl: string;
	manufacturerPlatePhotoUrl?: string | null;
	assetTagPhotoUrl?: string | null;
	boundingBox?: BoundingBox | null;
	photoHashes?: PhotoHashes | null;
	status: SlimFormStatus;
	extractedData?: ExtractedAssetData | null;
	editedData?: EditedAssetData | null;
	errorMessage?: string | null;
	errorDetails?: Record<string, unknown> | null;
	assetId?: number | null;
	assetTag?: string | null;
	rejectionReason?: string | null;
	createdAt: string;
	processedAt?: string | null;
	reviewedAt?: string | null;
	approvedAt?: string | null;
	updatedAt: string;
	// Joined data
	userName?: string;
	userEmail?: string;
	locationName?: string;
	locationAddress?: string;
	assetTypeName?: string;
}

/**
 * Request to create a new SlimForm submission
 */
export interface CreateSlimFormSubmissionRequest {
	locationId: number;
	overviewPhotoUrl: string;
	manufacturerPlatePhotoUrl?: string;
	assetTagPhotoUrl?: string;
	boundingBox?: BoundingBox;
	additionalComment?: string;
	virtualAssetTag?: string;
	visionTagLeftPhotoUrl?: string;
	visionTagRightPhotoUrl?: string;
	editedData?: EditedAssetData; // For Visual Tag 2.0 GPS data
	photoHashes?: PhotoHashes; // SHA-256 hashes for OCR cache dedup
}

/**
 * Request to update SlimForm submission data
 */
export interface UpdateSlimFormSubmissionRequest {
	editedData?: EditedAssetData;
	status?: SlimFormStatus;
	reviewedAt?: string;
}

/**
 * Paginated search results
 */
export interface SlimFormSearchResult {
	data: SlimFormSubmission[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

/**
 * Creates a new SlimForm submission
 */
export const createSlimFormSubmission = async (
	data: CreateSlimFormSubmissionRequest,
): Promise<SlimFormSubmission> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submit`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to create SlimForm submission");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Gets all SlimForm submissions for the current user's organization
 */
export const getSlimFormSubmissions = async (
	locationId?: number,
	status?: SlimFormStatus,
): Promise<SlimFormSubmission[]> => {
	const token = await auth.currentUser?.getIdToken();
	const params = new URLSearchParams();

	if (locationId) params.append("locationId", locationId.toString());
	if (status) params.append("status", status);

	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions${params.toString() ? `?${params.toString()}` : ""}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to retrieve submissions");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Searches SlimForm submissions with filters
 */
export const searchSlimFormSubmissions = async (params: {
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortOrder?: string;
	locationIds?: number[];
	statuses?: SlimFormStatus[];
	startDate?: string;
	endDate?: string;
}): Promise<SlimFormSearchResult> => {
	const token = await auth.currentUser?.getIdToken();
	const searchParams = new URLSearchParams();

	if (params.page) searchParams.append("page", params.page.toString());
	if (params.pageSize)
		searchParams.append("pageSize", params.pageSize.toString());
	if (params.sortBy) searchParams.append("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.append("sortOrder", params.sortOrder);
	if (params.locationIds?.length)
		searchParams.append("locationIds", params.locationIds.join(","));
	if (params.statuses?.length)
		searchParams.append("statuses", params.statuses.join(","));
	if (params.startDate) searchParams.append("startDate", params.startDate);
	if (params.endDate) searchParams.append("endDate", params.endDate);

	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/search?${searchParams.toString()}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to search submissions");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Gets submission counts by status
 */
export const getSlimFormSubmissionCounts = async (): Promise<
	Record<SlimFormStatus, number>
> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/counts`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to retrieve counts");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Gets a specific SlimForm submission by ID
 */
export const getSlimFormSubmission = async (
	submissionId: number,
): Promise<SlimFormSubmission> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/${submissionId}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to retrieve submission");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Updates a SlimForm submission
 */
export const updateSlimFormSubmission = async (
	submissionId: number,
	data: UpdateSlimFormSubmissionRequest,
): Promise<SlimFormSubmission> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/${submissionId}`;

	const response = await fetch(url, {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to update submission");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Approves a SlimForm submission and creates an asset
 */
export const approveSlimFormSubmission = async (
	submissionId: number,
): Promise<{ submissionId: number; assetId: number }> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/${submissionId}/approve`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to approve submission");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Rejects a SlimForm submission
 */
export const rejectSlimFormSubmission = async (
	submissionId: number,
	reason: string,
): Promise<SlimFormSubmission> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/${submissionId}/reject`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ reason }),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to reject submission");
	}

	const result = await response.json();
	return result.data;
};

/**
 * Manually triggers reprocessing of a submission
 */
export const reprocessSlimFormSubmission = async (
	submissionId: number,
): Promise<void> => {
	const token = await auth.currentUser?.getIdToken();
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/${submissionId}/reprocess`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Failed to reprocess submission");
	}
};

/**
 * Results of batch auto-approval process
 */
export interface BatchAutoApprovalResults {
	statusFixed: number;
	total: number;
	eligible: number;
	approved: number;
	failed: number;
	rejected: number;
	details: Array<{
		submissionId: number;
		status: "approved" | "not_eligible" | "failed" | "rejected";
		assetId?: number;
		error?: string;
	}>;
}

/**
 * Batch check and auto-approve all eligible PROCESSED submissions
 *
 * @param limit - Optional limit on number of submissions to process (default: no limit, process all)
 */
export const batchAutoApproveSubmissions = async (
	limit?: number,
): Promise<BatchAutoApprovalResults> => {
	const token = await auth.currentUser?.getIdToken();
	// Only add limit param if explicitly specified
	const params = limit !== undefined && limit > 0 ? `?limit=${limit}` : "";
	const url = `${VITE_CORE_API_URL}${CORE_URL}/slim-form/submissions/batch-auto-approve${params}`;

	// Set a generous timeout for batch processing (5 minutes)
	// This is a long-running operation that can take 60-120+ seconds for large batches
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000); // 5 minutes

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
			},
			signal: controller.signal,
		});

		clearTimeout(timeoutId);

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.message || "Failed to run batch auto-approval");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		clearTimeout(timeoutId);
		if (error instanceof Error && error.name === "AbortError") {
			throw new Error(
				"Batch auto-approval timed out after 5 minutes. Please try again with a smaller batch or contact support.",
			);
		}
		throw error;
	}
};
